/**
 * Doctor self-registration from the public site (free listing), SMS sign-in, and a listing doctor's
 * own practices. Mirrors medopd-backend's doctorlisting package.
 */
import { fetchApi, fetchGetFileApi, fetchMultipartApi } from '../client';
import { captureStaffSession } from '../sessionToken';
import type { ClinicProfileUpdate, ClinicProfileView, OpeningHours, PhotoView, Qualification } from './publicProfile';
import type { PagedResponse } from '../../../types/pagination';
import { buildQS } from '../helpers';

export interface CodeSent {
  challengeId: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface SignupVerified {
  grant: string;
  grantExpiresInSeconds: number;
  /** The number already has a doctor account: sign in instead. */
  alreadyRegistered: boolean;
}

export type PracticeRelationship = 'OWNER' | 'VISITING';

export interface PracticeInput {
  relationship: PracticeRelationship;
  name: string;
  address: string;
  locality?: string;
  city: string;
  state?: string;
  pincode: string;
  phone: string;
  fee?: number | null;
  hours: OpeningHours[];
}

export interface SignupCommand {
  grant: string;
  mobile: string;
  name: string;
  email: string;
  gender?: 'female' | 'male' | 'other' | '';
  specialization: string;
  /** Coded specialty (medopd-docs 155 S6): GENERAL, DENTAL, OPHTHALMOLOGY or another specialty-pack key. */
  specialtyKey?: string;
  practiceStartYear?: number | null;
  languages: string[];
  qualifications: Qualification[];
  registrationNumber: string;
  registrationCouncil: string;
  registrationYear?: number | null;
  practices: PracticeInput[];
  termsAccepted: boolean;
  listingConsent: boolean;
  /** From api.emailVerification.verifyCode (purpose DOCTOR_SIGNUP) for this same email. */
  emailGrant: string;
}

export interface PracticeDetailsUpdate {
  name: string;
  address: string;
  city: string;
  state?: string;
  pincode: string;
  phone: string;
  fee?: number | null;
  /** Appointment length for online booking: 10, 15, 20, 30, 45 or 60. */
  slotMinutes?: number;
}

export interface PracticeView {
  clinicId: string;
  relationship: PracticeRelationship;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  phone?: string | null;
  fee?: number | null;
  /** Patients can request appointments online here. */
  onlineBooking: boolean;
  slotMinutes: number;
  page: ClinicProfileView;
}

// ─── Appointments (Phase 18) ────────────────────────────────────────────────────────────────────

export type DeliveryOutcome = 'SENT' | 'NOT_AVAILABLE' | 'NOT_CONFIGURED' | 'FAILED';
/** What actually reached the patient after an action. */
export interface Delivery { email: DeliveryOutcome; sms: DeliveryOutcome }

export interface ProposalView { id: string; when: string; status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED'; answerBy: string }

/** Day-of status changes a doctor can make (server-decided per appointment, see BookingItem.actions). */
export type BookingStatusAction = 'ARRIVED' | 'START' | 'DONE' | 'NO_SHOW' | 'UNDO';

export interface BookingItem {
  tokenId: string;
  clinicId: string;
  practiceName?: string | null;
  status: string;
  date: string;
  time?: string | null;
  when?: string | null;
  tokenNumber?: number | null;
  patientName?: string | null;
  patientAge?: string | null;
  patientGender?: string | null;
  contactMobile?: string | null;
  bookedFor?: string | null;
  reference?: string | null;
  reason?: string | null;
  requestedAt?: string | null;
  expiresAt?: string | null;
  proposal?: ProposalView | null;
  declineReason?: string | null;
  cancelledAt?: string | null;
  /** PATIENT | DOCTOR | CLINIC (moved appointments never appear as cancelled) */
  cancelledBy?: string | null;
  cancelReason?: string | null;
  /** The fee paid when booking, when this request had one. */
  payment?: BookingPaymentInfo | null;
  doctorId?: string | null;
  doctorName?: string | null;
  /** IN_PERSON | VIDEO */
  consultationMode?: 'IN_PERSON' | 'VIDEO' | null;
  /** WEBSITE (public site) | PATIENT_APP (signed-in patient) | CLINIC (booked at the desk) */
  source?: 'WEBSITE' | 'PATIENT_APP' | 'CLINIC' | null;
  /** The video visit's state (SCHEDULED, WAITING, ACTIVE, ENDED, CANCELLED, EXPIRED) once one exists. */
  videoState?: string | null;
  /** When the patient was marked as arrived. */
  arrivedAt?: string | null;
  /** What the doctor can do with it right now (CONFIRM, DECLINE, PROPOSE, CANCEL, ARRIVED, START, DONE, NO_SHOW, UNDO). */
  actions?: string[];
  /** Pre-visit check-in form (doc 84): can staff send it now, and how far it got. Absent when there is nothing to show. */
  checkin?: { canSend: boolean; status?: string | null; linkSentAt?: string | null; linkSentVia?: string | null } | null;
  /** The patient, so the shared appointment actions can open the assisted check-in (medopd-docs 128). */
  patientId?: string | null;
}

export interface BookingPaymentInfo { status: 'PAID' | 'REFUND_REQUESTED' | 'REFUNDED' | 'REFUND_FAILED'; amount: number; problem?: string | null }

/** A clinic's Razorpay account as the settings show it — never the secrets, only whether they're set. */
export interface PaymentGatewayView {
  configured: boolean;
  keyId?: string | null;
  connectionStatus?: string | null;
  lastValidationError?: string | null;
  keySecretConfigured: boolean;
  webhookSecretConfigured: boolean;
  /** Path on the API ("/api/payments/razorpay/webhook/{clinicId}"); prefix with the API base URL. */
  webhookPath: string;
}

export interface BookingPaymentSetting {
  collectAtBooking: boolean;
  amount?: number | null;
  consultationFee?: number | null;
  effectiveAmount?: number | null;
  gatewayReady: boolean;
  /** Patients are actually asked to pay (chosen, account ready, and an amount). */
  active: boolean;
}

export interface PracticePayments { gateway: PaymentGatewayView; setting: BookingPaymentSetting }

/** Every tab of the doctor's Appointments screen (past, cancelled and not confirmed cover the last 60 days). */
export interface BookingInbox {
  requests: BookingItem[];
  upcoming: BookingItem[];
  past: BookingItem[];
  cancelled: BookingItem[];
  notConfirmed: BookingItem[];
  /** Every appointment in the requested date range, whatever its state (only when a range was asked for). */
  all?: BookingItem[];
  from?: string | null;
  to?: string | null;
}

/** The paged tabs (medopd-docs 117). */
export type BookingTab = 'requests' | 'upcoming' | 'past' | 'cancelled' | 'notConfirmed' | 'all';

/** Each tab's size and the stat cards' figures (a page can't give them). */
export interface BookingCounts {
  requests: number;
  upcoming: number;
  past: number;
  cancelled: number;
  notConfirmed: number;
  all: number;
  upcomingToday: number;
  upcomingThisWeek: number;
  upcomingVideo: number;
}

/** GET /doctor/bookings/summary: the counts and the practices to filter by. */
export interface BookingSummary {
  counts: BookingCounts;
  practices: { id: string; name: string }[];
  from?: string | null;
  to?: string | null;
}

/** Someone who booked with the doctor in the last year, across all their practices. */
export interface BookingPatient {
  patientId: string;
  name: string;
  age?: string | null;
  gender?: string | null;
  contactMobile?: string | null;
  appointments: number;
  cancelled: number;
  lastVisit?: string | null;
  nextVisit?: string | null;
  practices: string[];
}
/** {@code testLink}: the patient's link, only in local/dev test mode (never in production). */
export interface BookingActionResult { item: BookingItem; delivery: Delivery; testLink?: string | null }
export interface FreeTime { start: string; end: string }

/** A login response (the same shape as a password login), or an MFA challenge when the account has MFA on. */
export interface SessionResult {
  user?: { id: string }; mfaRequired?: boolean; mfaPendingToken?: string;
  /** SMS sign-in only: the account must prove its email first (pendingToken, challengeId, emailHint …). */
  emailVerificationRequired?: boolean;
}

const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body), skipBodyLog: true });
const put = (body: unknown) => ({ method: 'PUT', body: JSON.stringify(body) });
const practice = (clinicId: string) => `/api/doctor/practices/${encodeURIComponent(clinicId)}`;

export const doctorListingModule = {
  /** The public specialty catalogue (code + name), for the specialization picker. */
  specialties: () => fetchApi<{ code: string; name: string; doctors: number }[]>('/api/public/directory/specialties'),

  signup: {
    requestCode: (mobile: string) => fetchApi<CodeSent>('/api/public/doctor-signup/otp/request', post({ mobile })),
    verifyCode: (challengeId: string, mobile: string, code: string) =>
      fetchApi<SignupVerified>('/api/public/doctor-signup/otp/verify', post({ challengeId, mobile, code })),
    /** Creates the listing and starts a session (same response as a password login). */
    submit: async (cmd: SignupCommand) => {
      const response = await fetchApi<SessionResult>('/api/public/doctor-signup', post(cmd));
      captureStaffSession(response);
      return response;
    },
  },

  signIn: {
    requestCode: (mobile: string) => fetchApi<CodeSent>('/api/auth/doctor-otp/request', post({ mobile })),
    /** Starts a session (or asks for MFA when the account has it on), like a password login. */
    verify: async (challengeId: string, mobile: string, code: string) => {
      const response = await fetchApi<SessionResult>('/api/auth/doctor-otp/verify', post({ challengeId, mobile, code }));
      captureStaffSession(response);
      return response;
    },
    /** When verify answered emailVerificationRequired: the emailed code finishes the sign-in (same answer as verify). */
    completeEmailVerification: async (pendingToken: string, challengeId: string, code: string) => {
      const response = await fetchApi<SessionResult>('/api/auth/doctor-otp/email-verification/complete',
        post({ pendingToken, challengeId, code }));
      captureStaffSession(response);
      return response;
    },
  },

  /** Payment at booking for one of the doctor's practices: their own Razorpay account and the setting. */
  payments: (clinicId: string) => {
    const base = `/api/doctor/practices/${encodeURIComponent(clinicId)}/payments`;
    return {
      get: () => fetchApi<PracticePayments>(base),
      connect: (keyId: string, keySecret: string, webhookSecret: string) =>
        fetchApi<PracticePayments>(`${base}/gateway`, { ...put({ keyId, keySecret, webhookSecret }), skipBodyLog: true }),
      disconnect: () => fetchApi<PracticePayments>(`${base}/gateway`, { method: 'DELETE' }),
      update: (collectAtBooking: boolean, amount: number | null) => fetchApi<PracticePayments>(base, put({ collectAtBooking, amount })),
    };
  },

  bookings: {
    retryRefund: (clinicId: string, tokenId: string) =>
      fetchApi<BookingItem>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/retry-refund`, { method: 'POST' }),
    /** {@code range}: the days Past / Cancelled / Not confirmed and All cover (medopd-docs 95). */
    inbox: (range?: { from: string; to: string }) =>
      fetchApi<BookingInbox>(`/api/doctor/bookings${range ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}` : ''}`),
    freeTimes: (clinicId: string, date: string) =>
      fetchApi<FreeTime[]>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/free-times?date=${encodeURIComponent(date)}`),
    confirm: (clinicId: string, tokenId: string) =>
      fetchApi<BookingActionResult>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/confirm`, { method: 'POST' }),
    decline: (clinicId: string, tokenId: string, reason: string) =>
      fetchApi<BookingActionResult>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/decline`, post({ reason })),
    cancel: (clinicId: string, tokenId: string, reason: string) =>
      fetchApi<BookingActionResult>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/cancel`, post({ reason })),
    patients: () => fetchApi<BookingPatient[]>('/api/doctor/bookings/patients'),
    /** One page of a tab at the chosen practices; statuses narrow the All tab (medopd-docs 117). */
    inboxPage: (tab: BookingTab, filter: { clinicIds?: string[]; statuses?: string[]; from?: string; to?: string },
      page: number, size: number) =>
      fetchApi<PagedResponse<BookingItem>>(`/api/doctor/bookings${buildQS({ ...filter, tab, page, size })}`),
    /** Each tab's count, the stat cards and the practices to filter by. */
    summary: (filter: { clinicIds?: string[]; from?: string; to?: string }) =>
      fetchApi<BookingSummary>(`/api/doctor/bookings/summary${buildQS(filter)}`),
    /** One page of the patients, searchable by name or mobile. */
    patientsPage: (q: string, page: number, size: number) =>
      fetchApi<PagedResponse<BookingPatient>>(`/api/doctor/bookings/patients${buildQS({ q, page, size })}`),
    /** Record what happened on the day: arrived, started, done, didn't come, or undo. */
    status: (clinicId: string, tokenId: string, action: BookingStatusAction) =>
      fetchApi<BookingItem>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/status`, post({ action })),
    propose: (clinicId: string, tokenId: string, date: string, time: string, note?: string) =>
      fetchApi<BookingActionResult>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/${encodeURIComponent(tokenId)}/propose`,
        post({ date, time, note })),
    withdraw: (clinicId: string, proposalId: string) =>
      fetchApi<BookingItem>(`/api/doctor/bookings/${encodeURIComponent(clinicId)}/proposals/${encodeURIComponent(proposalId)}/withdraw`, { method: 'POST' }),
  },

  practices: {
    list: () => fetchApi<PracticeView[]>('/api/doctor/practices'),
    add: (input: PracticeInput) => fetchApi<PracticeView>('/api/doctor/practices', post(input)),
    updateDetails: (clinicId: string, u: PracticeDetailsUpdate) => fetchApi<PracticeView>(practice(clinicId), put(u)),
    updatePage: (clinicId: string, u: ClinicProfileUpdate) => fetchApi<PracticeView>(`${practice(clinicId)}/page`, put(u)),
    publish: (clinicId: string) => fetchApi<PracticeView>(`${practice(clinicId)}/publish`, { method: 'POST' }),
    unpublish: (clinicId: string) => fetchApi<PracticeView>(`${practice(clinicId)}/unpublish`, { method: 'POST' }),
    setOnlineBooking: (clinicId: string, enabled: boolean) =>
      fetchApi<PracticeView>(`${practice(clinicId)}/online-booking`, put({ enabled })),
    photos: (clinicId: string) => ({
      list: () => fetchApi<PhotoView[]>(`${practice(clinicId)}/photos`),
      upload: (file: File, caption: string, noPatientsConfirmed: boolean) => {
        const form = new FormData();
        form.append('photo', file);
        form.append('caption', caption);
        form.append('noPatientsConfirmed', String(noPatientsConfirmed));
        return fetchMultipartApi<PhotoView>(`${practice(clinicId)}/photos`, form);
      },
      remove: (id: string) => fetchApi<void>(`${practice(clinicId)}/photos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      image: (id: string) => fetchGetFileApi(`${practice(clinicId)}/photos/${encodeURIComponent(id)}/image`),
      reorder: (photoIds: string[]) => fetchApi<PhotoView[]>(`${practice(clinicId)}/photos/order`, put({ photoIds })),
    }),
  },
};
